import { useState, useRef, useEffect, useCallback, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { io, Socket } from 'socket.io-client';
import { api } from '../api';
import { API_URL } from '../config';
import { getToken } from '../auth';
import { useTheme } from '../context/ThemeContext';
import { useSupportUrl, SUPPORT_FACEBOOK_ENABLED } from '../hooks/useSupportUrl';
import Icon from './Icon';

type ChatSender = 'user' | 'ai' | 'admin' | 'system';
interface ChatAttachment {
  url: string;
  publicId?: string | null;
  mime?: string | null;
  width?: number | null;
  height?: number | null;
}
interface ChatMessage {
  id?: string;
  sender: ChatSender;
  content: string;
  escalate?: boolean;
  createdAt?: string;
  attachment?: ChatAttachment | null;
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'];

interface UploadSignResponse {
  signature: string;
  timestamp: number;
  apiKey: string;
  cloudName: string;
  folder: string;
  uploadPreset: string;
}

interface CloudinaryUploadResponse {
  secure_url: string;
  public_id: string;
  width?: number;
  height?: number;
  format?: string;
  resource_type?: string;
}

const MAX_HISTORY = 10;

interface ThreadResponse {
  aiEnabled: boolean;
  messages: Array<{
    id: string;
    sender: ChatSender;
    content: string;
    escalate: boolean;
    createdAt: string;
    attachment?: ChatAttachment | null;
  }>;
}

interface ChatPostResponse {
  reply: string;
  escalate: boolean;
  aiEnabled: boolean;
}

interface SocketMessagePayload {
  threadId?: string;
  message: {
    id: string;
    sender: ChatSender;
    content: string;
    escalate: boolean;
    createdAt: string;
    attachment?: ChatAttachment | null;
  };
}

interface SocketAiTogglePayload {
  threadId?: string;
  aiEnabled: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SupportChat({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { colors, isDark } = useTheme();
  const supportUrl = useSupportUrl();

  const greeting: ChatMessage = { sender: 'ai', content: t('components.supportChat.greeting') };
  const [messages, setMessages] = useState<ChatMessage[]>([greeting]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [showHumanHint, setShowHumanHint] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Messenger-style "draft" — once the user picks an image it lives
  // here until they hit Send (so they can type a caption alongside).
  // The local preview URL is a blob: URL we create from the picked
  // File; the uploaded URL is filled in once Cloudinary responds.
  const [pendingAttachment, setPendingAttachment] = useState<{
    previewUrl: string;
    mime: string;
    uploaded: ChatAttachment | null;
  } | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Dedupe by id — appends only if no existing message shares the id.
  const appendIfNew = useCallback((msg: ChatMessage) => {
    setMessages(prev => {
      if (msg.id && prev.some(m => m.id && m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Load thread history + open socket while the panel is open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let socket: Socket | null = null;

    void (async () => {
      // 1) Hydrate from server.
      try {
        const res = await api.get<ThreadResponse>('/chat/thread');
        if (cancelled) return;
        const serverMsgs = res.data?.messages ?? [];
        if (serverMsgs.length === 0) {
          setMessages([{ sender: 'ai', content: t('components.supportChat.greeting') }]);
        } else {
          setMessages(serverMsgs.map(m => ({
            id: m.id,
            sender: m.sender,
            content: m.content,
            escalate: m.escalate,
            createdAt: m.createdAt,
            attachment: m.attachment ?? null,
          })));
          // Reflect any prior escalation marker.
          if (serverMsgs.some(m => m.escalate)) setEscalated(true);
        }
        setAiEnabled(!!res.data?.aiEnabled);
      } catch {
        // Leave the existing greeting/state alone on failure.
      }

      // 2) Open a dedicated socket for chat events. The shared
      // SocketContext doesn't expose the raw socket, so we connect our
      // own scoped to this panel's lifetime. The server places this
      // socket in the `user:{id}` room based on the auth token.
      if (cancelled) return;
      const token = await getToken();
      if (cancelled) return;
      socket = io(API_URL, {
        transports: ['websocket'],
        reconnectionDelay: 3000,
        auth: token ? { token } : undefined,
      });
      socketRef.current = socket;

      socket.on('chat:message', (payload: SocketMessagePayload) => {
        if (!payload?.message) return;
        const m = payload.message;
        appendIfNew({
          id: m.id,
          sender: m.sender,
          content: m.content,
          escalate: m.escalate,
          createdAt: m.createdAt,
          attachment: m.attachment ?? null,
        });
        if (m.escalate) setEscalated(true);
      });

      socket.on('chat:ai-toggled', (payload: SocketAiTogglePayload) => {
        if (typeof payload?.aiEnabled === 'boolean') {
          setAiEnabled(payload.aiEnabled);
        }
      });
    })();

    return () => {
      cancelled = true;
      if (socket) {
        socket.off('chat:message');
        socket.off('chat:ai-toggled');
        socket.disconnect();
      }
      socketRef.current = null;
    };
  }, [open, appendIfNew, t]);

  const openFb = useCallback(() => {
    window.open(supportUrl, '_blank', 'noopener');
  }, [supportUrl]);

  const sendInner = useCallback(
    async (text: string, attachment: ChatAttachment | null) => {
      if (loading) return;
      if (!text && !attachment) return;
      setShowHumanHint(false);

      // Optimistic user bubble (no id — server will assign one; socket
      // echo will be deduped if it arrives with an id we already have).
      const optimistic: ChatMessage = {
        sender: 'user',
        content: text,
        attachment,
      };
      const next: ChatMessage[] = [...messages, optimistic];
      setMessages(next);
      setInput('');
      setLoading(true);
      try {
        // Backwards-compat payload: map our sender to {user, model}.
        // Image bubbles still carry their (often empty) text — Gemini
        // ignores them server-side when attachment is set.
        const trimmed = next.slice(-MAX_HISTORY).map(m => ({
          role: m.sender === 'user' ? 'user' : 'model',
          content: m.content || (m.attachment ? '[image]' : ''),
        }));
        const res = await api.post<ChatPostResponse>('/chat', {
          messages: trimmed,
          ...(attachment ? { attachment } : {}),
        });
        const reply = res.data?.reply ?? '';
        const esc = !!res.data?.escalate;
        const nowAiEnabled = !!res.data?.aiEnabled;
        setAiEnabled(nowAiEnabled);

        if (!nowAiEnabled) {
          // Don't render an empty AI bubble — show the hint instead.
          setShowHumanHint(true);
        } else if (reply) {
          appendIfNew({ sender: 'ai', content: reply, escalate: esc });
        } else if (attachment) {
          // Attachment-only path: backend intentionally returns empty
          // reply because Gemini can't see images. Surface the "team
          // will respond" hint so the user knows their image landed.
          setShowHumanHint(true);
        }
        if (esc) setEscalated(true);
      } catch {
        // No automatic Facebook redirect — keep the user in-app and
        // wait for a team member. Admins can still escalate to FB
        // manually from the inbox if needed.
        setShowHumanHint(true);
      } finally {
        setLoading(false);
      }
    },
    [loading, messages, appendIfNew],
  );

  const clearPending = useCallback(() => {
    setPendingAttachment(prev => {
      if (prev?.previewUrl) {
        try { URL.revokeObjectURL(prev.previewUrl); } catch { /* noop */ }
      }
      return null;
    });
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    const attachment = pendingAttachment?.uploaded ?? null;
    if (!text && !attachment) return;
    if (loading || uploading) return;
    clearPending();
    await sendInner(text, attachment);
  }, [input, pendingAttachment, loading, uploading, clearPending, sendInner]);

  const onPickFile = useCallback(() => {
    if (pendingAttachment || uploading || loading) return;
    setUploadError(null);
    fileInputRef.current?.click();
  }, [pendingAttachment, uploading, loading]);

  const onFileChosen = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input value so picking the same file twice fires a change.
      if (e.target) e.target.value = '';
      if (!file) return;
      if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
        setUploadError(t('components.supportChat.errorBadType'));
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setUploadError(t('components.supportChat.errorTooBig'));
        return;
      }
      if (uploading || loading) return;

      // Show the preview chip right away (Messenger-style optimism)
      // while the upload runs in the background.
      const previewUrl = URL.createObjectURL(file);
      setPendingAttachment({ previewUrl, mime: file.type, uploaded: null });
      setUploading(true);
      setUploadError(null);
      try {
        const sign = await api.post<UploadSignResponse>('/chat/upload-sign');
        const s = sign.data;
        const form = new FormData();
        form.append('file', file);
        form.append('api_key', s.apiKey);
        form.append('timestamp', String(s.timestamp));
        form.append('signature', s.signature);
        form.append('folder', s.folder);
        form.append('upload_preset', s.uploadPreset);
        const cloudRes = await fetch(
          `https://api.cloudinary.com/v1_1/${s.cloudName}/image/upload`,
          { method: 'POST', body: form },
        );
        // Read the body once so we can surface Cloudinary's own error
        // message (its 400 responses include `{ error: { message } }`
        // that names the actual problem — invalid signature, missing
        // preset, etc.).
        const cloudText = await cloudRes.text();
        if (!cloudRes.ok) {
          let detail = `HTTP ${cloudRes.status}`;
          try {
            const parsed = JSON.parse(cloudText);
            detail = parsed?.error?.message || detail;
          } catch { /* keep HTTP status */ }
          // eslint-disable-next-line no-console
          console.error('Cloudinary upload failed', detail, cloudText);
          throw new Error(`Cloudinary: ${detail}`);
        }
        const cloud = JSON.parse(cloudText) as CloudinaryUploadResponse;
        const uploaded: ChatAttachment = {
          url: cloud.secure_url,
          publicId: cloud.public_id,
          mime: file.type,
          width: cloud.width ?? null,
          height: cloud.height ?? null,
        };
        setPendingAttachment(prev =>
          prev ? { ...prev, uploaded } : prev,
        );
      } catch (err) {
        const detail = err instanceof Error ? err.message : '';
        setUploadError(
          detail
            ? t('components.supportChat.errorUploadDetail', { detail })
            : t('components.supportChat.errorUpload'),
        );
        // Discard the failed pending — user can pick a new one.
        clearPending();
      } finally {
        setUploading(false);
      }
    },
    [uploading, loading, clearPending, t],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) return null;

  const showFbButton = SUPPORT_FACEBOOK_ENABLED && escalated;

  const userBubble: CSSProperties = {
    alignSelf: 'flex-end',
    backgroundColor: colors.accentDark,
    color: '#020617',
    borderRadius: '14px 14px 4px 14px',
  };
  const aiBubble: CSSProperties = {
    alignSelf: 'flex-start',
    backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
    color: colors.text,
    borderRadius: '14px 14px 14px 4px',
  };
  const adminBubble: CSSProperties = {
    alignSelf: 'flex-start',
    backgroundColor: isDark ? 'rgba(24,119,242,0.14)' : 'rgba(24,119,242,0.08)',
    color: colors.text,
    borderRadius: '14px 14px 14px 4px',
    borderLeft: '3px solid #1877F2',
  };

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(2,6,23,0.35)',
          zIndex: 1000,
          animation: 'aga-support-fade-in 180ms ease-out',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('components.supportChat.dialogLabel')}
        style={{
          position: 'fixed',
          right: 'max(16px, env(safe-area-inset-right, 0px))',
          bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          width: 'min(360px, calc(100vw - 24px))',
          height: 'min(540px, calc(100dvh - 32px))',
          display: 'flex', flexDirection: 'column',
          backgroundColor: colors.card,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: 18,
          boxShadow: '0 18px 48px rgba(2,6,23,0.35)',
          zIndex: 1001,
          overflow: 'hidden',
          animation: 'aga-support-slide-up 220ms ease-out',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 10,
              backgroundColor: colors.accentSoft,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name="headset" size={14} color={colors.accentText} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: colors.text, lineHeight: 1.1 }}>{t('components.supportChat.header')}</div>
              <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2 }}>
                {aiEnabled ? t('components.supportChat.statusAi') : t('components.supportChat.statusAgent')}
              </div>
            </div>
          </div>
          <button
            aria-label={t('components.supportChat.closeLabel')}
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: 18,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name="times" size={14} color={colors.textSub} />
          </button>
        </div>

        <div style={{
          padding: '8px 14px',
          fontSize: 11, color: colors.textMuted,
          backgroundColor: isDark ? 'rgba(20,241,209,0.06)' : 'rgba(20,241,209,0.10)',
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}>
          {aiEnabled
            ? t('components.supportChat.disclaimerAi')
            : t('components.supportChat.bannerAgent')}
        </div>

        <div
          ref={listRef}
          style={{
            flex: 1, minHeight: 0, overflowY: 'auto',
            padding: 14,
            display: 'flex', flexDirection: 'column', gap: 8,
            backgroundColor: colors.bg,
          }}
        >
          {messages.map((m, i) => {
            const key = m.id ?? `idx-${i}`;
            if (m.sender === 'system') {
              return (
                <div
                  key={key}
                  style={{
                    alignSelf: 'center',
                    backgroundColor: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(148,163,184,0.18)',
                    color: colors.textMuted,
                    fontSize: 11,
                    padding: '4px 10px',
                    borderRadius: 999,
                    opacity: 0.85,
                    maxWidth: '90%',
                    textAlign: 'center',
                  }}
                >
                  {m.content}
                </div>
              );
            }
            if (m.sender === 'admin') {
              return (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '82%' }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: '#1877F2',
                    marginBottom: 2, letterSpacing: 0.2,
                  }}>
                    {t('components.supportChat.supportTeam')}
                  </div>
                  {m.attachment?.url && (
                    <img
                      src={m.attachment.url}
                      alt="Attachment"
                      onClick={() => setLightboxUrl(m.attachment!.url)}
                      style={{
                        maxWidth: 240,
                        maxHeight: 240,
                        borderRadius: 12,
                        marginBottom: m.content ? 4 : 0,
                        cursor: 'zoom-in',
                        display: 'block',
                      }}
                    />
                  )}
                  {m.content && (
                    <div
                      style={{
                        ...adminBubble,
                        padding: '8px 12px',
                        fontSize: 13,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {m.content}
                    </div>
                  )}
                </div>
              );
            }
            const bubble = m.sender === 'user' ? userBubble : aiBubble;
            const isUser = m.sender === 'user';
            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isUser ? 'flex-end' : 'flex-start',
                  maxWidth: '82%',
                  alignSelf: isUser ? 'flex-end' : 'flex-start',
                }}
              >
                {m.attachment?.url && (
                  <img
                    src={m.attachment.url}
                    alt={t('components.supportChat.attachmentAlt')}
                    onClick={() => setLightboxUrl(m.attachment!.url)}
                    style={{
                      maxWidth: 240,
                      maxHeight: 240,
                      borderRadius: 12,
                      marginBottom: m.content ? 4 : 0,
                      cursor: 'zoom-in',
                      display: 'block',
                    }}
                  />
                )}
                {m.content && (
                  <div
                    style={{
                      ...bubble,
                      padding: '8px 12px',
                      fontSize: 13,
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.content}
                  </div>
                )}
              </div>
            );
          })}
          {loading && (
            <div
              style={{
                ...aiBubble,
                maxWidth: 70,
                padding: '10px 14px',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
              aria-label={t('components.supportChat.typingLabel')}
            >
              <span style={dotStyle(0)} />
              <span style={dotStyle(1)} />
              <span style={dotStyle(2)} />
            </div>
          )}
          {showHumanHint && !loading && (
            <div
              style={{
                alignSelf: 'flex-start',
                fontSize: 11,
                color: colors.textMuted,
                fontStyle: 'italic',
                padding: '2px 4px',
              }}
            >
              {t('components.supportChat.teamReplySoon')}
            </div>
          )}
          {showFbButton && (
            <button
              onClick={openFb}
              style={{
                marginTop: 8, alignSelf: 'flex-start',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', borderRadius: 999,
                backgroundColor: '#1877F2', color: '#fff',
                border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 800, minHeight: 40,
                boxShadow: '0 4px 14px rgba(24,119,242,0.35)',
              }}
            >
              <Icon name="facebook" size={14} color="#fff" />
              {t('components.supportChat.fbButton')}
            </button>
          )}
        </div>

        {uploadError && (
          <div style={{
            padding: '6px 14px',
            fontSize: 11,
            color: '#ef4444',
            backgroundColor: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.10)',
            borderTop: `1px solid ${colors.cardBorder}`,
          }}>
            {uploadError}
          </div>
        )}
        {pendingAttachment && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px',
            borderTop: `1px solid ${colors.cardBorder}`,
            backgroundColor: isDark ? 'rgba(20,241,209,0.06)' : 'rgba(20,241,209,0.08)',
          }}>
            <div style={{
              position: 'relative',
              width: 56, height: 56, borderRadius: 10,
              overflow: 'hidden', flexShrink: 0,
              backgroundColor: '#020617',
            }}>
              <img
                src={pendingAttachment.previewUrl}
                alt={t('components.supportChat.pendingAlt')}
                style={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  opacity: pendingAttachment.uploaded ? 1 : 0.55,
                }}
              />
              {!pendingAttachment.uploaded && (
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.85)',
                    borderTopColor: 'transparent',
                    animation: 'aga-support-spin 0.8s linear infinite',
                  }} />
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.text }}>
                {pendingAttachment.uploaded ? t('components.supportChat.readyToSend') : t('components.supportChat.uploadingImage')}
              </div>
              <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2 }}>
                {t('components.supportChat.captionHint')}
              </div>
            </div>
            <button
              onClick={clearPending}
              disabled={loading}
              aria-label={t('components.supportChat.removeImage')}
              title={t('components.supportChat.removeImage')}
              style={{
                width: 28, height: 28, borderRadius: 14,
                background: 'transparent',
                border: `1px solid ${colors.cardBorder}`,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name="times" size={10} color={colors.textSub} />
            </button>
          </div>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: 10,
          borderTop: `1px solid ${colors.cardBorder}`,
          backgroundColor: colors.card,
        }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onFileChosen}
            style={{ display: 'none' }}
          />
          <button
            onClick={onPickFile}
            disabled={!!pendingAttachment || uploading || loading}
            aria-label={t('components.supportChat.attachLabel')}
            title={t('components.supportChat.attachLabel')}
            style={{
              width: 40, height: 40, borderRadius: 20,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: pendingAttachment || uploading || loading ? 'default' : 'pointer',
              opacity: pendingAttachment || uploading || loading ? 0.55 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              color: colors.accentDark,
            }}
          >
            <Icon name="image" size={20} color={colors.accentDark} />
          </button>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={loading}
            placeholder={pendingAttachment ? t('components.supportChat.captionPlaceholder') : t('components.supportChat.inputPlaceholder')}
            aria-label={t('components.supportChat.messageLabel')}
            style={{
              flex: 1, minWidth: 0,
              padding: '10px 12px',
              borderRadius: 999,
              border: `1px solid ${colors.cardBorder}`,
              backgroundColor: colors.bg,
              color: colors.text,
              fontSize: 13, outline: 'none',
            }}
          />
          <button
            onClick={send}
            disabled={
              loading ||
              uploading ||
              (!input.trim() && !pendingAttachment?.uploaded)
            }
            aria-label={t('components.supportChat.sendLabel')}
            style={{
              width: 40, height: 40, borderRadius: 20,
              backgroundColor: colors.accentDark,
              border: 'none',
              cursor:
                loading ||
                uploading ||
                (!input.trim() && !pendingAttachment?.uploaded)
                  ? 'default'
                  : 'pointer',
              opacity:
                loading ||
                uploading ||
                (!input.trim() && !pendingAttachment?.uploaded)
                  ? 0.55
                  : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="paper-plane" size={14} color="#020617" />
          </button>
        </div>
      </div>

      {lightboxUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('components.supportChat.imagePreviewLabel')}
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(2,6,23,0.85)',
            zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
            cursor: 'zoom-out',
          }}
        >
          <img
            src={lightboxUrl}
            alt={t('components.supportChat.fullSizeAlt')}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: 8,
              boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes aga-support-slide-up {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes aga-support-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes aga-support-dot {
          0%, 80%, 100% { transform: translateY(0);  opacity: 0.4; }
          40%           { transform: translateY(-3px); opacity: 1; }
        }
        @keyframes aga-support-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

function dotStyle(i: number): CSSProperties {
  return {
    width: 6, height: 6, borderRadius: '50%',
    backgroundColor: '#94a3b8',
    display: 'inline-block',
    animation: `aga-support-dot 1.2s ${i * 0.15}s infinite ease-in-out`,
  };
}
